<template>
  <div class="bg-white rounded-[12px] border border-gray-200 shadow-custom overflow-hidden mb-5 max-h-[600px]">
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-200">
            <th class="text-left px-6 py-4 font-bold text-gray-500 text-xs uppercase tracking-wider">
              {{ headerValidity }}
            </th>
            <th class="text-right px-6 py-4 font-bold text-gray-500 text-xs uppercase tracking-wider">
              {{ headerOfficial }}
            </th>
            <th class="text-right px-6 py-4 font-bold text-gray-500 text-xs uppercase tracking-wider">
              {{ headerFee }}
            </th>
            <th class="text-right px-6 py-4 font-bold text-gray-500 text-xs uppercase tracking-wider bg-blue-50">
              {{ headerTotal }}
            </th>
            <th class="px-6 py-4"></th>
          </tr>
        </thead>
        <tbody v-if="rows.length">
          <tr
            v-for="(row, index) in rows"
            :key="row.id"
            :class="['hover:bg-gray-50 transition-colors', index === rows.length - 1 ? '' : 'border-b border-gray-100']"
          >
            <td class="px-6 py-5">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                  <span class="material-symbols-outlined text-blue-900 text-lg">{{ row.icon }}</span>
                </div>
                <span class="font-bold text-gray-900">{{ row.label }}</span>
              </div>
            </td>
            <td class="px-6 py-5 text-right text-gray-600 font-medium">
              {{ formatPrice(row.official) }}
            </td>
            <td class="px-6 py-5 text-right text-gray-600 font-medium">
              {{ formatPrice(row.fee) }}
            </td>
            <td class="px-6 py-5 text-right bg-blue-50">
              <span class="font-black text-blue-900 text-base">
                {{ formatPrice(row.total) }}
              </span>
            </td>
            <td class="px-6 py-5 text-right">
              <button
                type="button"
                class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-small font-bold text-xs transition-all whitespace-nowrap"
                @click="onBuy(row)"
              >
                {{ ctaLabel }}
              </button>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="5" class="px-6 py-5 text-center text-gray-500 text-sm">
              {{ emptyLabel }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';

defineOptions({ name: 'VignettePrices' });

const store = useStore();

const products = computed(() => store.getters['form/products'] || []);
const texts = computed(() => store.getters.texts || {});
const storeCurrency = computed(() => store.getters['currency/currentCurrency'] || {});
const currencies = computed(() => store.getters.currencies || []);

const currencySymbol = computed(() =>
    storeCurrency.value?.symbol ||
    currencies.value[0]?.symbol ||
    '€'
);
const currencyConversion = computed(() =>
    Number(storeCurrency.value?.conversion ?? 1) || 1
);

const headerValidity = computed(() =>
    texts.value['vignette-prices.blade.php-th-validity'] ?? 'Validity'
);

const headerOfficial = computed(() =>
    texts.value['vignette-prices.blade.php-th-official'] ?? 'Official Price'
);

const headerFee = computed(() =>
    texts.value['vignette-prices.blade.php-th-fee'] ?? 'Service Fee'
);

const headerTotal = computed(() =>
    texts.value['vignette-prices.blade.php-th-total'] ?? 'Total Price'
);

const ctaLabel = computed(() =>
    texts.value['vignette-prices.blade.php-cta-buy'] ?? 'Buy Now'
);

const emptyLabel = computed(() =>
    texts.value['vignette-prices.blade.php-empty'] ?? 'No vignette options available.'
);

/** Only show vignette rows tied to these CMS page IDs */
const ALLOWED_PAGE_IDS = new Set([72, 77, 82, 87]);

/**
 * Rows for table (unique durations)
 */
const rows = computed(() => {
    const byDuration = new Map();
    (products.value || []).forEach((product) => {
        if (!product || typeof product !== 'object') return;

        const pageId = Number(product.page_id);
        if (!Number.isFinite(pageId) || !ALLOWED_PAGE_IDS.has(pageId)) return;

        const days = getDurationDays(product);
        if (!days) return;

        if (!byDuration.has(days)) {
            byDuration.set(days, product);
        }
    });

    const sorted = Array.from(byDuration.entries()).sort((a, b) => a[0] - b[0]);

    return sorted.map(([days, product]) => {
        const safeProduct = product && typeof product === 'object' ? product : {};
        
        const rawTitle =
            safeProduct.category?.translated?.title ||
            safeProduct.translated?.title ||
            '';
        const label = cleanTitle(rawTitle) || `${days} days`;

        const { official, fee, total } = getPriceParts(safeProduct);

        return {
            id: safeProduct.id ?? null,
            label,
            icon: pickIcon(days),
            days,
            official,
            fee,
            total,
        };
    });
});

/**
 * Sum extensions by type/code (case-insensitive)
 */
function sumExtensionsByKeys(extensions, keys) {
    if (!Array.isArray(extensions) || !keys?.length) return 0;
    let sum = 0;
    const lowers = keys.map(k => String(k).toLowerCase());

    extensions.forEach((ext) => {
        if (!ext || typeof ext !== 'object') return;
        const type = String(ext.type ?? '').toLowerCase();
        const code = String(ext.code ?? '').toLowerCase();
        const matches = lowers.some(k => type.includes(k) || code.includes(k));
        if (!matches) return;

        const v = Number(ext.value);
        if (!Number.isNaN(v)) sum += v;
    });

    return sum;
}
/**
 * Derive official, fee, total from product + extensions
 */
function getPriceParts(product) {
    if (!product || typeof product !== 'object') {
        return { official: 0, fee: 0, total: 0 };
    }

    const exts = Array.isArray(product.extensions) ? product.extensions : [];
    let total = Number(product.price) || 0;

    const fee = sumExtensionsByKeys(exts, ['fee', 'service']);
    const explicitOfficial = product.price;

    let official;
    if (explicitOfficial) {
        official = explicitOfficial;
        if (!total) total = official + fee;
    } else if (total) {
        official = Math.max(total - fee, 0);
    } else {
        official = 0;
    }

    if (!total) total = official + fee;

    const rate = currencyConversion.value;
    const convertedOfficial = official * rate;
    const convertedFee = fee * rate;
    const convertedTotal = convertedOfficial + convertedFee;

    return {
        official: convertedOfficial,
        fee: convertedFee,
        total: convertedTotal,
    };
}

/**
 * Extract days from a string (fallback)
 */
function extractDays(title) {
    const match = title.match(/\d+/);
    return match ? Number(match[0]) : null;
}

/**
 * Remove "Vignette" prefix
 */
function cleanTitle(title) {
    if (!title) return '';
    return title.replace(/^Vignette\s*/i, '');
}

/**
 * Price formatter
 */
function formatPrice(amount) {
    const value = Number(amount) || 0;

    return `${currencySymbol.value}${value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}`;
}

/**
 * Determine icon by duration in days.
 * Use glyph names included in the Material Symbols subset (see npm run build:icons).
 */
function pickIcon(days) {
    if (days === 1) return 'calendar_today';
    if (days === 10) return 'date_range';
    if (days === 30) return 'calendar_month';
    if (days >= 365) return 'autorenew';
    return 'local_shipping';
}

/**
 * Derive duration in days from product/category
 */
function getDurationDays(product) {
    if (!product || typeof product !== 'object') return null;

    if (typeof product.duration_days === 'number') return product.duration_days;
    if (typeof product.duration === 'number' && product.duration >= 1) {
        return product.duration;
    }

    const titleSource =
        product.category?.translated?.title ||
        product.translated?.title ||
        '';
    const daysFromTitle = titleSource ? extractDays(String(titleSource)) : null;
    if (daysFromTitle) return daysFromTitle;

    const pathSource =
        product.category?.path ||
        product.path ||
        '';
    const match = String(pathSource).toLowerCase().match(/(\d+)[-_ ]*days?/);
    if (match) return Number(match[1]);

    return null;
}

/**
 * Buy button
 */
async function onBuy(row) {
    if (!row?.id) return;

    store.commit('form/SET_FORM_DATA', {
        product_id: row.id,
        selected_duration: row.days ?? null,
    });

    store.dispatch('form/saveDataToLocalStorage');

    window.location.href = '/buy-vignette';
}
/**
 * Load data on mount
 */
onMounted(async () => {
    if (!products.value.length) {
        try {
            await store.dispatch('form/loadInitialData');
        } catch {
            // ignore load errors
        }
    }
});
</script>
